import React, { Fragment } from 'react'
import { useTable, useSortBy, useExpanded } from 'react-table'
import { useMediaQuery } from 'react-responsive'

/**
 * PaginatedTable - Server-side paginated table component
 * Use this component when you need API-level pagination
 * 
 * Required Props:
 * - data: Current page data array
 * - columns: Column definitions
 * - currentPage: Current page number (1-based)
 * - totalPages: Total number of pages
 * - totalRecords: Total number of records
 * - onPageChange: Callback function (page) => void
 * - pageSize: Number of records per page
 * 
 * Optional Props:
 * - All other Table2 props (searchable, export, extraButtons, etc.)
 */
const PaginatedTable = (props) => {
    const isMobile = useMediaQuery({ query: `(max-width: 576px)` });
    
    const data = React.useMemo(
        () => {
            if (Array.isArray(props.data)) { return [...props.data] } else {
                return ([])
            }
        },
        [props.data]
    )

    const hiddenColumns = React.useMemo(() => {
        if (Array.isArray(props.hiddenColumns) && isMobile)
            return props.hiddenColumns
        else if (Array.isArray(props.desktopHiddenColumns) && !isMobile) {
            return props.desktopHiddenColumns
        }
        else {
            return []
        }
    }, [props.hiddenColumns, props.desktopHiddenColumns, isMobile])

    const columns = React.useMemo(
        () => [...props.columns],
        [props.columns]
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({
        columns,
        data,
        initialState: { 
            hiddenColumns: hiddenColumns 
        },
    },
        useSortBy,
        useExpanded
    );

    // Server-side pagination handlers
    const handleFirstPage = () => {
        if (props.onPageChange) {
            props.onPageChange(1);
        }
    };

    const handlePreviousPage = () => {
        if (props.onPageChange && props.currentPage > 1) {
            props.onPageChange(props.currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (props.onPageChange && props.currentPage < props.totalPages) {
            props.onPageChange(props.currentPage + 1);
        }
    };

    const handleLastPage = () => {
        if (props.onPageChange) {
            props.onPageChange(props.totalPages);
        }
    };

    const canPreviousPage = props.currentPage > 1;
    const canNextPage = props.currentPage < props.totalPages;
    const startRecord = ((props.currentPage - 1) * props.pageSize) + 1;
    const endRecord = Math.min(props.currentPage * props.pageSize, props.totalRecords);

    return (
        <div className="paginated-table-template">
            {props.extraElements || props.extraButtons ? 
                <div className={`row d-flex justify-content-end ${props.hidden ? '' : 'mb-3'} align-items-end g-3 mx-0 px-0`}>
                    {props.extraElements?.length > 0 ? 
                        (props.extraElements?.length > 1 ? 
                            <>{props.extraElements.map(element => element.children)}</> : 
                            <div className="col-md-auto col-12 extra-elements">{props.extraElements.map(element => element.children)}</div>
                        ) : null}
                    <div className='col-md-auto'>
                        <div className='btn-group m-w-100'>
                            {props.extraButtons && props.extraButtons.map((button, idx) => {
                                return (
                                    <button 
                                        key={idx}
                                        title={button.title} 
                                        className={`${button.className}`} 
                                        onClick={e => { 
                                            e.preventDefault(); 
                                            if (button.onClick) { 
                                                button.onClick() 
                                            } 
                                        }}
                                    >
                                        {button.children}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div> : null}
            
            <div style={{ overflowX: "auto" }} hidden={props.hidden}>
                <table {...getTableProps()} className="table table-borderless">
                    {headerGroups.map(headerGroup => (
                        <colgroup key={headerGroup.id}>
                            {headerGroup.headers.map(column => (
                                <col key={column.id} span={1} style={{ minWidth: column.minWidth }} />
                            ))}
                        </colgroup>)
                    )}
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map(column => (
                                    <th
                                        key={column.id}
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        style={{ textAlign: column.textAlign }}
                                        className={column.disableSortBy ? '' : 'point'}
                                    >
                                        {column.render('Header')}
                                        <span>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? <i className="icon angle down" />
                                                    : <i className="icon angle up" />
                                                : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows?.length > 0 ? rows.map(row => {
                            prepareRow(row)
                            return (
                                <tr key={row.id} {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td key={cell.column.id} {...cell.getCellProps()}>
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            )
                        }) :
                            props.loading ? 
                                <tr><td colSpan={columns.length}>Please be patient while data loads...</td></tr> :
                                props.initialMessage ? 
                                    <tr><td colSpan={columns.length}>Please submit the report...</td></tr> :
                                    <tr><td colSpan={columns.length}>No Results Found</td></tr>
                        }
                    </tbody>
                </table>
            </div>
            
            {props.totalPages > 1 && !props.hidden ?
                <div className='row d-flex align-items-center justify-content-between mt-3 pe-0 me-0'>
                    <div className='col'>
                        <div className="pagination align-items-center row d-flex justify-content-start">
                            <div className='btn-group col-md-auto col-12'>
                                <button 
                                    className='btn btn-primary' 
                                    onClick={(e) => { e.preventDefault(); handleFirstPage(); }} 
                                    disabled={!canPreviousPage}
                                >
                                    {'<<'}
                                </button>{' '}
                                <button 
                                    className='btn btn-primary' 
                                    onClick={(e) => { e.preventDefault(); handlePreviousPage(); }} 
                                    disabled={!canPreviousPage}
                                >
                                    {'<'}
                                </button>{' '}
                                <button 
                                    className='btn btn-primary' 
                                    onClick={(e) => { e.preventDefault(); handleNextPage(); }} 
                                    disabled={!canNextPage}
                                >
                                    {'>'}
                                </button>{' '}
                                <button 
                                    className='btn btn-primary' 
                                    onClick={(e) => { e.preventDefault(); handleLastPage(); }} 
                                    disabled={!canNextPage}
                                >
                                    {'>>'}
                                </button>{' '}
                            </div>
                            <span className='col-md-auto col-12'>
                                Page{' '}
                                <strong>
                                    {props.currentPage} of {props.totalPages}
                                </strong>{' '}
                            </span>
                        </div>
                    </div>
                    <div className='col-md-auto col-12'>
                        <strong>
                            {startRecord}-{endRecord} of {props.totalRecords} records
                        </strong>
                    </div>
                </div> : null}
        </div>
    )
}

export default PaginatedTable
